const THOM_FREE_GIFT_TIMER_KEY = 'thom_free_gift_v2_timer';
const THOM_FREE_GIFT_PENDING_KEY = 'thom_free_gift_v2_pending';
const THOM_FREE_GIFT_TIMER_DURATION = 60 * 60 * 1000;

function getThomFreeGiftStateFromSections(sections) {
  const drawerHtml = sections?.['cart-drawer'];
  if (!drawerHtml) return null;

  const picker = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('thom-free-gift-picker');
  if (!picker) return null;

  return {
    eligible: picker.dataset.eligible === 'true',
    selectedVariantId: picker.dataset.selectedVariantId || '',
    selectedChoice: picker.dataset.selectedChoice || '',
  };
}

function dispatchThomFreeGiftState(state, source) {
  window.dispatchEvent(
    new CustomEvent('thomdogpaw:free-gift-state', {
      detail: {
        ...state,
        source,
      },
    }),
  );
}

class ThomFreeGiftPicker extends HTMLElement {
  static mutationInFlight = false;

  connectedCallback() {
    this.choiceButtons = [...this.querySelectorAll('[data-thom-free-gift-choice], [data-thom-free-gift-surprise]')];
    this.choiceButtons.forEach((button) => {
      button.dataset.initiallyDisabled = String(button.disabled);
      button.addEventListener('click', this.handleChoice);
    });

    this.auditPromotionalLines();
  }

  disconnectedCallback() {
    this.choiceButtons?.forEach((button) => button.removeEventListener('click', this.handleChoice));
  }

  handleChoice = (event) => {
    const button = event.currentTarget;
    if (button.disabled || ThomFreeGiftPicker.mutationInFlight) return;

    if (button.hasAttribute('data-thom-free-gift-surprise')) {
      const candidates = this.choiceButtons.filter(
        (candidate) =>
          candidate.hasAttribute('data-thom-free-gift-choice') &&
          candidate.dataset.variantId &&
          candidate.dataset.initiallyDisabled !== 'true',
      );
      if (!candidates.length) return;

      const chosen = candidates[Math.floor(Math.random() * candidates.length)];
      this.selectGift(chosen.dataset.variantId, chosen.dataset.sku, 'surprise');
      return;
    }

    this.selectGift(button.dataset.variantId, button.dataset.sku, 'explicit');
  };

  get promotionalLines() {
    return [...this.querySelectorAll('[data-thom-free-gift-line]')].map((line) => ({
      key: line.dataset.lineKey,
      quantity: Number(line.dataset.quantity),
      variantId: line.dataset.variantId,
    }));
  }

  async auditPromotionalLines() {
    if (ThomFreeGiftPicker.mutationInFlight) return;

    const lines = this.promotionalLines;
    if (!lines.length) return;

    if (this.dataset.eligible !== 'true') {
      const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
      await this.updatePromotionalLines(updates, 'removed');
      return;
    }

    const selectedVariantId = this.dataset.selectedVariantId;
    const keptLine = [...lines].reverse().find((line) => line.variantId === selectedVariantId) || lines[0];
    const needsNormalization = lines.length > 1 || keptLine.quantity !== 1;
    if (!needsNormalization) return;

    const updates = Object.fromEntries(lines.map((line) => [line.key, line.key === keptLine.key ? 1 : 0]));
    await this.updatePromotionalLines(updates, 'changed');
  }

  async selectGift(variantId, sku, choice, options = {}) {
    if (!variantId || ThomFreeGiftPicker.mutationInFlight) return false;
    if (this.dataset.selectedVariantId === variantId && this.dataset.selectedChoice === choice) return true;

    const { openDrawer = true, focusPicker = true, source = this.dataset.surface } = options;
    ThomFreeGiftPicker.mutationInFlight = true;
    this.setBusy(true);
    const isChange = this.promotionalLines.length > 0;

    try {
      const existingUpdates = Object.fromEntries(this.promotionalLines.map((line) => [line.key, 0]));
      if (Object.keys(existingUpdates).length) {
        await this.request(window.routes.cart_update_url, { updates: existingUpdates });
      }

      const state = await this.request(window.routes.cart_add_url, {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _thomdogpaw_free_gift: 'true',
              _gift_choice: choice,
            },
          },
        ],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      });

      const eventName = isChange
        ? 'free_gift_changed'
        : choice === 'surprise'
          ? 'free_gift_surprise_selected'
          : 'free_gift_selected';
      this.trackSelection(eventName, sku, source);
      this.renderState(state, variantId, 'added', openDrawer, focusPicker, source);
      return true;
    } catch (error) {
      this.announce('error');
      console.error('THOMDOGPAW free gift update failed.', error);
      return false;
    } finally {
      ThomFreeGiftPicker.mutationInFlight = false;
      this.setBusy(false);
    }
  }

  async updatePromotionalLines(updates, messageKey) {
    ThomFreeGiftPicker.mutationInFlight = true;
    this.setBusy(true);

    try {
      const state = await this.request(window.routes.cart_update_url, {
        updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      });
      this.renderState(state, null, messageKey, false, false, 'cart-audit');
    } catch (error) {
      this.announce('error');
      console.error('THOMDOGPAW free gift cart repair failed.', error);
    } finally {
      ThomFreeGiftPicker.mutationInFlight = false;
      this.setBusy(false);
    }
  }

  async request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const state = await response.json();

    if (!response.ok || state.errors || state.status >= 400) {
      throw new Error(state.description || state.message || state.errors || response.statusText);
    }

    return state;
  }

  renderState(state, focusVariantId, messageKey, openDrawer = true, focusPicker = true, source = this.dataset.surface) {
    if (window.location.pathname.replace(/\/$/, '') === window.routes.cart_url.replace(/\/$/, '')) {
      window.location.reload();
      return;
    }

    const freeGiftState = getThomFreeGiftStateFromSections(state.sections) || {
      eligible: this.dataset.eligible === 'true',
      selectedVariantId: focusVariantId ? String(focusVariantId) : '',
      selectedChoice: focusVariantId ? 'explicit' : '',
    };
    const drawer = document.querySelector('cart-drawer');
    if (state.sections && drawer) {
      if (openDrawer || drawer.classList.contains('active')) {
        drawer.renderContents(state);
      } else {
        const drawerHtml = state.sections['cart-drawer'];
        const iconHtml = state.sections['cart-icon-bubble'];
        if (drawerHtml) {
          const nextDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (nextDrawer && currentDrawer) currentDrawer.innerHTML = nextDrawer.innerHTML;
        }
        if (iconHtml) {
          const nextIcon = new DOMParser().parseFromString(iconHtml, 'text/html').querySelector('.shopify-section');
          const currentIcon = document.querySelector('#cart-icon-bubble');
          if (nextIcon && currentIcon) currentIcon.innerHTML = nextIcon.innerHTML;
        }
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      }
    }

    dispatchThomFreeGiftState(freeGiftState, source);

    window.requestAnimationFrame(() => {
      const nextPicker = document.querySelector('cart-drawer thom-free-gift-picker');
      nextPicker?.announce(messageKey);
      if (focusVariantId && focusPicker) {
        nextPicker?.querySelector(`[data-variant-id="${CSS.escape(String(focusVariantId))}"]`)?.focus();
      }
    });
  }

  setBusy(isBusy) {
    this.setAttribute('aria-busy', String(isBusy));
    this.choiceButtons?.forEach((button) => {
      button.disabled = isBusy || button.dataset.initiallyDisabled === 'true';
    });
  }

  announce(messageKey) {
    const status = this.querySelector('[data-thom-free-gift-status]');
    const messages = window.thomFreeGiftStrings || {};
    if (status && messages[messageKey]) status.textContent = messages[messageKey];
  }

  trackSelection(eventName, sku, source = this.dataset.surface) {
    const detail = { sku, source };
    window.dispatchEvent(new CustomEvent(`thomdogpaw:${eventName}`, { detail }));
    window.dataLayer?.push({ event: eventName, free_gift_sku: sku });
  }
}

if (!customElements.get('thom-free-gift-picker')) {
  customElements.define('thom-free-gift-picker', ThomFreeGiftPicker);
}

class ThomFreeGiftWidget extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-thom-free-gift-open]');
    this.closeButton = this.querySelector('[data-thom-free-gift-close]');
    this.backdrop = this.querySelector('[data-thom-free-gift-backdrop]');
    this.panel = this.querySelector('[data-thom-free-gift-panel]');
    this.choiceButtons = [...this.querySelectorAll('[data-thom-free-gift-panel-choice], [data-thom-free-gift-panel-surprise]')];
    this.explicitChoiceButtons = this.choiceButtons.filter((button) =>
      button.hasAttribute('data-thom-free-gift-panel-choice'),
    );
    this.timerNodes = [...this.querySelectorAll('[data-thom-free-gift-timer]')];
    this.selectionNode = this.querySelector('[data-thom-free-gift-selection]');
    this.pendingNode = this.querySelector('[data-thom-free-gift-pending]');
    this.postTimerNode = this.querySelector('[data-thom-free-gift-post-timer]');
    this.statusNode = this.querySelector('[data-thom-free-gift-widget-status]');
    this.eligible = this.dataset.eligible === 'true';
    this.actualVariantId = this.dataset.selectedVariantId || '';
    this.actualChoice = this.dataset.selectedChoice || '';

    this.choiceButtons.forEach((button) => {
      button.dataset.initiallyDisabled = String(button.disabled);
      button.addEventListener('click', this.handleChoice);
    });
    this.pendingSelection = this.readPendingSelection();
    this.openButton?.addEventListener('click', this.openPanel);
    this.closeButton?.addEventListener('click', this.closePanel);
    this.backdrop?.addEventListener('click', this.closePanel);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('thomdogpaw:free-gift-state', this.handleSharedState);
    window.addEventListener('storage', this.handleStorage);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }

    if (this.actualVariantId) this.clearPendingSelection();
    this.startTimer();
    this.renderSelection();
    this.schedulePendingApplication();
  }

  disconnectedCallback() {
    this.choiceButtons?.forEach((button) => button.removeEventListener('click', this.handleChoice));
    this.openButton?.removeEventListener('click', this.openPanel);
    this.closeButton?.removeEventListener('click', this.closePanel);
    this.backdrop?.removeEventListener('click', this.closePanel);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('thomdogpaw:free-gift-state', this.handleSharedState);
    window.removeEventListener('storage', this.handleStorage);
    this.cartUpdateUnsubscriber?.();
    window.clearInterval(this.timerInterval);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.pendingApplicationTimer);
  }

  openPanel = () => {
    if (!this.panel || !this.backdrop || this.isPanelOpen) return;

    window.clearTimeout(this.closeTimer);
    this.isPanelOpen = true;
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');

    window.requestAnimationFrame(() => {
      this.panel.classList.add('is-open');
      this.backdrop.classList.add('is-open');
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.closeButton || this.panel);
    });
  };

  closePanel = () => {
    if (!this.panel || !this.backdrop || !this.isPanelOpen) return;

    this.isPanelOpen = false;
    this.panel.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openButton);

    this.closeTimer = window.setTimeout(() => {
      if (this.isPanelOpen) return;
      this.panel.hidden = true;
      this.backdrop.hidden = true;
    }, 260);
  };

  handleKeydown = (event) => {
    if (event.code === 'Escape' && this.isPanelOpen) this.closePanel();
  };

  handleChoice = async (event) => {
    const button = event.currentTarget;
    if (button.disabled || this.isBusy) return;

    const currentChoice = this.actualVariantId ? this.actualChoice : this.pendingSelection?.choice;
    if (button.hasAttribute('data-thom-free-gift-panel-surprise') && currentChoice === 'surprise') return;

    let selectedButton = button;
    let choice = 'explicit';
    if (button.hasAttribute('data-thom-free-gift-panel-surprise')) {
      const candidates = this.explicitChoiceButtons.filter(
        (candidate) => candidate.dataset.variantId && candidate.dataset.initiallyDisabled !== 'true',
      );
      if (!candidates.length) return;
      selectedButton = candidates[Math.floor(Math.random() * candidates.length)];
      choice = 'surprise';
    }

    const selection = {
      variantId: selectedButton.dataset.variantId,
      sku: selectedButton.dataset.sku,
      choice,
      name: button.dataset.giftName,
    };
    if (!selection.variantId) return;

    if (!this.eligible) {
      this.pendingSelection = selection;
      this.writePendingSelection(selection);
      this.renderSelection();
      this.announce(this.dataset.savedMessage);
      return;
    }

    await this.applySelection(selection, 'widget');
  };

  async applySelection(selection, source) {
    const picker = document.querySelector('cart-drawer thom-free-gift-picker');
    if (!picker || typeof picker.selectGift !== 'function' || ThomFreeGiftPicker.mutationInFlight) return false;

    this.setBusy(true);
    const succeeded = await picker.selectGift(selection.variantId, selection.sku, selection.choice, {
      openDrawer: false,
      focusPicker: false,
      source,
    });
    this.setBusy(false);

    if (!succeeded) this.announce(this.dataset.errorMessage);
    return succeeded;
  }

  handleSharedState = (event) => {
    const state = event.detail;
    if (!state) return;
    this.syncState(state);
  };

  handleCartUpdate = (event) => {
    const state = getThomFreeGiftStateFromSections(event?.cartData?.sections);
    if (state) this.syncState(state);
  };

  syncState(state) {
    const previousActualVariantId = this.actualVariantId;
    this.eligible = Boolean(state.eligible);
    this.actualVariantId = state.selectedVariantId || '';
    this.actualChoice = state.selectedChoice || '';

    if (this.actualVariantId || (previousActualVariantId && !this.actualVariantId)) {
      this.clearPendingSelection();
    } else {
      this.pendingSelection = this.readPendingSelection();
    }

    this.renderSelection();
    this.schedulePendingApplication();
  }

  schedulePendingApplication() {
    window.clearTimeout(this.pendingApplicationTimer);
    if (!this.eligible || this.actualVariantId || !this.pendingSelection || ThomFreeGiftPicker.mutationInFlight) return;

    this.pendingApplicationTimer = window.setTimeout(async () => {
      if (!this.eligible || this.actualVariantId || !this.pendingSelection) return;
      const pending = { ...this.pendingSelection };
      await this.applySelection(pending, 'pending-preselection');
    }, 160);
  }

  renderSelection() {
    const selection = this.actualVariantId
      ? {
          variantId: this.actualVariantId,
          choice: this.actualChoice,
          name: this.getSelectionName(this.actualVariantId, this.actualChoice),
        }
      : this.pendingSelection;

    this.choiceButtons.forEach((button) => {
      const isSurprise = button.hasAttribute('data-thom-free-gift-panel-surprise');
      const selected = Boolean(
        selection &&
          ((selection.choice === 'surprise' && isSurprise) ||
            (selection.choice !== 'surprise' && !isSurprise && button.dataset.variantId === selection.variantId)),
      );
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
      const action = button.querySelector('[data-thom-free-gift-action]');
      if (action) action.textContent = selected ? this.dataset.selectedLabel : this.dataset.chooseLabel;
    });

    if (this.selectionNode) {
      const hasSelection = Boolean(selection?.name);
      this.selectionNode.hidden = !hasSelection;
      this.selectionNode.textContent = hasSelection
        ? this.dataset.selectedTemplate.replace('[gift]', selection.name)
        : '';
    }
    if (this.pendingNode) this.pendingNode.hidden = !(this.pendingSelection && !this.actualVariantId);
  }

  getSelectionName(variantId, choice) {
    if (choice === 'surprise') {
      return this.querySelector('[data-thom-free-gift-panel-surprise]')?.dataset.giftName || '';
    }
    return (
      this.explicitChoiceButtons.find((button) => button.dataset.variantId === String(variantId))?.dataset.giftName || ''
    );
  }

  setBusy(isBusy) {
    this.isBusy = isBusy;
    this.panel?.setAttribute('aria-busy', String(isBusy));
    this.choiceButtons.forEach((button) => {
      button.disabled = isBusy || button.dataset.initiallyDisabled === 'true';
    });
  }

  announce(message) {
    if (this.statusNode && message) this.statusNode.textContent = message;
  }

  readPendingSelection() {
    try {
      const pending = JSON.parse(window.localStorage.getItem(THOM_FREE_GIFT_PENDING_KEY));
      if (!pending?.variantId || !pending?.choice) return null;

      const configuredButton = this.explicitChoiceButtons?.find(
        (button) => button.dataset.variantId === String(pending.variantId),
      );
      if (!configuredButton || configuredButton.dataset.initiallyDisabled === 'true') {
        window.localStorage.removeItem(THOM_FREE_GIFT_PENDING_KEY);
        return null;
      }

      return {
        variantId: String(pending.variantId),
        sku: configuredButton.dataset.sku,
        choice: pending.choice === 'surprise' ? 'surprise' : 'explicit',
        name:
          pending.choice === 'surprise'
            ? this.querySelector('[data-thom-free-gift-panel-surprise]')?.dataset.giftName
            : configuredButton.dataset.giftName,
      };
    } catch (error) {
      window.localStorage.removeItem(THOM_FREE_GIFT_PENDING_KEY);
      return null;
    }
  }

  writePendingSelection(selection) {
    try {
      window.localStorage.setItem(THOM_FREE_GIFT_PENDING_KEY, JSON.stringify(selection));
    } catch (error) {
      console.warn('THOMDOGPAW free gift preselection could not be saved.', error);
    }
  }

  clearPendingSelection() {
    this.pendingSelection = null;
    try {
      window.localStorage.removeItem(THOM_FREE_GIFT_PENDING_KEY);
    } catch (error) {
      console.warn('THOMDOGPAW free gift preselection could not be cleared.', error);
    }
  }

  handleStorage = (event) => {
    if (event.key === THOM_FREE_GIFT_PENDING_KEY) {
      this.pendingSelection = this.readPendingSelection();
      this.renderSelection();
      this.schedulePendingApplication();
    }
    if (event.key === THOM_FREE_GIFT_TIMER_KEY) this.startTimer();
  };

  startTimer() {
    window.clearInterval(this.timerInterval);
    const now = Date.now();
    let timerState;

    try {
      timerState = JSON.parse(window.localStorage.getItem(THOM_FREE_GIFT_TIMER_KEY));
    } catch (error) {
      timerState = null;
    }

    const validTimer =
      Number.isFinite(timerState?.startedAt) &&
      Number.isFinite(timerState?.expiresAt) &&
      timerState.expiresAt >= timerState.startedAt;
    if (!validTimer) {
      timerState = {
        startedAt: now,
        expiresAt: now + THOM_FREE_GIFT_TIMER_DURATION,
      };
      try {
        window.localStorage.setItem(THOM_FREE_GIFT_TIMER_KEY, JSON.stringify(timerState));
      } catch (error) {
        console.warn('THOMDOGPAW free gift timer could not be saved.', error);
      }
    }

    this.timerState = timerState;
    this.updateTimer();
    if (timerState.expiresAt > now) {
      this.timerInterval = window.setInterval(() => this.updateTimer(), 1000);
    }
  }

  updateTimer() {
    const remaining = Math.max(0, this.timerState.expiresAt - Date.now());
    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const label = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    this.timerNodes.forEach((node) => {
      node.textContent = label;
    });

    const hasEnded = remaining <= 0;
    if (this.postTimerNode) this.postTimerNode.hidden = !hasEnded;
    if (hasEnded) window.clearInterval(this.timerInterval);
  }
}

if (!customElements.get('thom-free-gift-widget')) {
  customElements.define('thom-free-gift-widget', ThomFreeGiftWidget);
}
